/**
 * Student "My Assignments" due-date agenda (design AS2).
 *
 * Same layout as My Quizzes & Exams (shared components/agenda), plus:
 *  - drafts ("Draft saved" → Resume Draft) and allowed revisions (Revise Assignment)
 *  - points before submitting, the grade after
 *  - weekly assignments grouped into ONE row with a week tracker (W1…Wn),
 *    instead of one row per week. Chips for submitted / missed / current
 *    weeks are buttons that open that week.
 */
import { utcToChicago, getWeekEndDate } from '../../../utils/datetime/datetime-comprehensive';
import {
  toChicago, chicagoToday, chicagoNow, fmtTime, plural, tileFor, openTiming, submittedStatus,
  buildExtensionNote, extensionCta, assembleAgenda,
} from '../../agenda/agendaCore';
import { getAssignmentDueDate, getAssignmentUiKey } from '../useAssignments/statusHelpers';

const MAX_TRACKED_WEEKS = 26;

/** Titles are stored as (sanitized) HTML; the agenda shows plain text. */
export const htmlToText = (html) => {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = String(html);
    return (el.textContent || '').trim();
  }
  return String(html).replace(/<[^>]*>/g, '').trim();
};

const parseWeekList = (value) => {
  let list = value || [];
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch { list = []; }
  }
  return Array.isArray(list) ? list.map(Number) : [];
};

const isWeekly = (a) => a.allow_weekly_submissions === true || Number(a.allow_weekly_submissions) === 1;
const isExtra = (a) => a.is_extra_credit === true || a.is_extra_credit === 1;
const hasGrade = (a) => a.grade !== null && a.grade !== undefined && a.grade !== '';

/** Due date (Chicago DateTime) for week `w` of a weekly assignment — mirrors weeklyProcessing. */
function weekDueDate(base, w) {
  const overrides = base.weekly_overrides_by_week && typeof base.weekly_overrides_by_week === 'object'
    ? base.weekly_overrides_by_week : {};
  const override = overrides[w] ?? overrides[String(w)];
  const extra = override?.is_extra_credit === true || override?.is_extra_credit === 1;
  try {
    if (w === 1 && base.due_date) return utcToChicago(base.due_date);
    if (override?.due_date) return utcToChicago(override.due_date);
    if (extra) return null;
    if (base.start_date) {
      return getWeekEndDate(base.start_date, w, Array.isArray(base.break_periods) ? base.break_periods : []) || null;
    }
  } catch {
    return null;
  }
  return null;
}

function buildDetail(a, completed) {
  const points = Number(a.points);
  if (completed) {
    if (!hasGrade(a)) return { value: '', label: 'grade', emptyText: 'grade pending', emptyValue: 'Pending' };
    return { value: points > 0 ? `${Number(a.grade)}/${points}` : `${Number(a.grade)}`, label: 'grade' };
  }
  if (points > 0) return { value: `${points}`, label: isExtra(a) ? 'bonus points' : 'points' };
  return { value: '', label: 'points', emptyText: 'no points', emptyValue: 'None' };
}

/**
 * One assignment (or one week of a weekly assignment) → agenda row view model.
 */
export function buildAssignmentRow(a, ext, { today, getStatus, resubmissionAllowed }) {
  const status = getStatus(a);
  const completed = a.statusType === 'completed';
  const due = toChicago(getAssignmentDueDate(a));
  const submitted = toChicago(a.submitted_at);
  const blocked = !!ext?.submissionBlocked;
  const canRevise = completed && !!resubmissionAllowed[a.id];
  const isDraft = !completed && status === 'draft';

  let section;
  let pill;
  let time;
  if (completed) {
    section = 'completed';
    pill = submittedStatus(submitted, due);
    time = { label: 'Submitted', value: submitted ? fmtTime(submitted) : '' };
  } else {
    ({ section, status: pill, time } = openTiming(due, today, { blocked }));
  }

  const tags = [];
  if (isDraft && !blocked) tags.push({ text: 'Draft saved', tone: 'pending' });
  if (canRevise) tags.push({ text: 'Revision allowed', tone: 'later' });

  let cta;
  if (blocked) cta = extensionCta(ext);
  else if (canRevise) cta = { label: 'Revise Assignment', variant: 'primary', icon: 'revise', action: 'revise' };
  else if (completed) cta = { label: 'View Submission', variant: 'secondary', icon: 'file', action: 'view' };
  else if (isDraft) cta = { label: 'Resume Draft', variant: 'primary', icon: 'resume', action: 'submit' };
  else cta = { label: 'Start Assignment', variant: 'primary', icon: 'pencil', action: 'submit' };
  cta.week = a.week_number || null;

  const uiKey = getAssignmentUiKey(a);
  const weekly = isWeekly(a) && a.week_number;
  const title = htmlToText(a.title);
  return {
    id: uiKey,
    anchor: `assignment-row-${uiKey}`,
    assignment: a,
    section,
    title: weekly ? `Week ${a.week_number}: ${title}` : title,
    courseCode: a.course_code || '',
    courseName: a.course_name || (a.course_code ? '' : 'No course assigned'),
    typeLabel: isExtra(a) ? 'Extra Credit' : weekly ? `Weekly · Week ${a.week_number}` : 'Assignment',
    typeTone: isExtra(a) ? 'extra' : weekly ? 'weekly' : null,
    tile: tileFor(completed ? (submitted || due) : due),
    tileTone: pill.tone,
    status: pill,
    time,
    detail: buildDetail(a, completed),
    tags,
    extensionNote: buildExtensionNote(ext),
    courseTag: ext && !ext.courseActive && !ext.approvedActive ? { text: 'Course Ended', tone: 'neutral' } : null,
    cta,
    stripDue: completed ? null : due,
    stripTitle: title,
    sortDue: due ? due.toMillis() : Number.MAX_SAFE_INTEGER,
    sortSubmitted: submitted ? submitted.toMillis() : 0,
  };
}

/**
 * All visible week rows of one weekly assignment → a single row with a week tracker.
 * The row's status/button follow the "focus" week: the open week if there is one,
 * otherwise the earliest missed week, otherwise the latest submitted week.
 */
export function buildWeeklyGroupRow(weekRows, ext, ctx) {
  const rows = [...weekRows].sort((x, y) => Number(x.week_number) - Number(y.week_number));
  const base = rows[0];
  const now = chicagoNow();
  const byWeek = new Map(rows.map((r) => [Number(r.week_number), r]));
  const isPast = (r) => {
    const due = toChicago(getAssignmentDueDate(r));
    return !!due && due < now;
  };
  const open = rows.find((r) => r.statusType !== 'completed' && !isPast(r));
  const missed = rows.find((r) => r.statusType !== 'completed' && isPast(r));
  const done = rows.filter((r) => r.statusType === 'completed');
  const focus = open || missed || done[done.length - 1] || base;
  const focusWeek = Number(focus.week_number);

  const row = buildAssignmentRow(focus, ext, ctx);
  const title = htmlToText(base.title);

  const submittedWeeks = new Set(parseWeekList(base.submitted_week_numbers));
  const draftWeeks = new Set(parseWeekList(base.draft_week_numbers));
  const maxRaw = Number(base.max_weekly_submissions);
  const total = Math.min(MAX_TRACKED_WEEKS, Math.max(
    Number.isFinite(maxRaw) && maxRaw > 0 ? Math.floor(maxRaw) : 1,
    ...rows.map((r) => Number(r.week_number) || 1),
  ));

  let submittedCount = 0;
  let missedCount = 0;
  const weeks = [];
  for (let w = 1; w <= total; w += 1) {
    const due = weekDueDate(base, w);
    const weekRow = byWeek.get(w) || { ...base, week_number: w };
    let state;
    let action = null;
    let label;
    if (submittedWeeks.has(w)) {
      state = 'done';
      submittedCount += 1;
      action = 'view';
      label = `Week ${w}: submitted, view`;
    } else if (w === focusWeek && !ext?.submissionBlocked) {
      state = draftWeeks.has(w) ? 'draft' : 'current';
      action = row.cta.action;
      label = `Week ${w}: ${draftWeeks.has(w) ? 'draft saved' : 'this week'}${due ? `, due ${due.toFormat('LLL d')}` : ''}`;
    } else if (due && due < now) {
      state = 'missed';
      missedCount += 1;
      action = ext?.submissionBlocked ? null : 'submit';
      label = `Week ${w}: missed${action ? ', submit late' : ''}`;
    } else {
      state = 'future';
      label = `Week ${w}: ${due ? `due ${due.toFormat('LLL d')}` : 'not open yet'}`;
    }
    weeks.push({ week: w, state, action, title: label, assignment: weekRow });
  }
  const dueSoFar = weeks.filter((w) => w.state === 'done' || w.state === 'missed').length;
  const summary = dueSoFar > 0
    ? `${submittedCount} of ${dueSoFar} submitted${missedCount ? ` · ${missedCount} missed` : ''}`
    : '';

  const verb = { 'Start Assignment': 'Start', 'Resume Draft': 'Resume', 'View Submission': 'View', 'Revise Assignment': 'Revise' }[row.cta.label];
  return {
    ...row,
    id: `${base.id}`,
    anchor: `assignment-row-${base.id}`,
    title,
    typeLabel: `Weekly · ${plural(total, 'week')}`,
    typeTone: 'weekly',
    time: row.time.label === 'Due' || row.time.label === 'Was due'
      ? { ...row.time, label: `Week ${focusWeek} ${row.time.label.toLowerCase()}` }
      : row.time,
    detail: row.detail.label === 'points' ? { ...row.detail, label: 'points / week' } : row.detail,
    cta: verb ? { ...row.cta, label: `${verb} Week ${focusWeek}`, week: focusWeek, assignment: focus } : row.cta,
    stripTitle: `${title} W${focusWeek}`,
    weeks,
    weekSummary: summary ? { text: summary, missed: missedCount > 0 } : null,
  };
}

/**
 * filteredAssignments (already course/search filtered and sorted) → agenda.
 */
export function buildAssignmentAgenda(filteredAssignments, { getExtensionInfo, getStatus, resubmissionAllowed, sortBy }) {
  const today = chicagoToday();
  const ctx = { today, getStatus, resubmissionAllowed };
  const rows = [];
  const weeklyGroups = new Map();
  filteredAssignments.forEach((a) => {
    if (isWeekly(a) && a.week_number) {
      const key = String(a.id);
      if (!weeklyGroups.has(key)) {
        weeklyGroups.set(key, []);
        rows.push({ weeklyKey: key }); // keep the sort position of the first week seen
      }
      weeklyGroups.get(key).push(a);
    } else {
      rows.push(buildAssignmentRow(a, getExtensionInfo(a), ctx));
    }
  });
  const built = rows.map((r) => {
    if (!r.weeklyKey) return r;
    const weekRows = weeklyGroups.get(r.weeklyKey);
    return buildWeeklyGroupRow(weekRows, getExtensionInfo(weekRows[0]), ctx);
  });
  return assembleAgenda(built, { sortBy });
}
